import React, { useEffect } from "react";
import { ColorPickerItem } from "~components";
import cn from "classnames";
import { useProductContext } from "~hooks";
import * as styles from "./styles.module.scss";

/** ============================================================================
 * @component
 * Array of color selector buttons with layout configuration
 *
 * variant: {variantId: string, selected: boolean}
 * @param  {Variant[]}  variants  		Available color variants
 * @param  {string}  		selectedColor	Selected color object
 * @param  {string}  		itemClassName	ClassName for color items
 * @param  {boolean}  	square				Whether the buttons are square, otherwise is a circle
 * @param  {boolean}  	vertical			Whether the buttons are stacked vertically
 * @param  {boolean}  	display				Buttons are non-interactable
 * @param  {function}  	onChange   		Fired when item is clicked, color object is passed in
 * @param  {number}  		quantity   		Quantity user wants to add to cart
 * @param  {string}  		className   	For additional styling config
 * @return {node}
 */

const ColorPicker = ({
  variants,
  onChange,
  itemClassName,
  square,
  vertical,
  display,
  quantity,
  className
}) => {
  const { getVariantData } = useProductContext();

  const colors = [];

  variants?.forEach((variant) => {
    const { hex, orderRank, title, variantId } =
      getVariantData(variant.variantId) || {};
    const formattedVariant = {
      hex,
      orderRank,
      title,
      variantId,
      selected: variant.selected
    };
    colors.push(formattedVariant);
  });
  colors.sort((a, b) => (a.orderRank > b.orderRank ? 1 : -1));

  // Select first color by default
  useEffect(() => {
    if (!onChange) return;

    const selectedColor = colors.find((color) => color.selected);
    if (!selectedColor) {
      // Doesn't work if not timeout ¯\_(ツ)_/¯
      setTimeout(() => {
        onChange(colors?.[0]?.variantId);
      });
    }
  }, [colors]);

  return (
    <div
      className={cn(styles.container, className, {
        [styles.vertical]: vertical
      })}
    >
      {colors.map((variant) => (
        <ColorPickerItem
          key={variant.variantId}
          variantId={variant.variantId}
          hex={variant.hex}
          title={variant.title}
          className={cn(styles.colorButton, itemClassName)}
          square={square}
          quantity={quantity}
          display={display}
          handleClick={onChange}
          isSelected={variant.selected}
        />
      ))}
    </div>
  );
};

export default ColorPicker;
